/** OSS 连接测试失败时 API 返回的 detail 结构（与 media_storage.php 一致） */
export type OssTestFailureDetail = {
  ok?: boolean;
  err?: string;
  http_code?: number;
  oss_code?: string;
  oss_message?: string;
  user_message?: string;
  body?: string;
  curl_error?: string;
};

const OSS_CODE_HINTS: Record<string, string> = {
  SecondLevelDomainForbidden:
    '该 Bucket 须用三级域名访问（bucket.endpoint），不能使用路径风格 URL。请部署最新服务端后重试。',
  AccessDenied:
    '无写入权限：请为 RAM 用户授权该 Bucket 的 oss:PutObject、oss:GetObject（参见开通指引中的策略示例）。',
  SignatureDoesNotMatch: 'AccessKey Secret 与 ID 不匹配，或服务器时间偏差过大。',
  InvalidAccessKeyId: 'AccessKey ID 无效或已被禁用。',
  NoSuchBucket: 'Bucket 不存在，或 Endpoint 地域与 Bucket 所在地域不一致。',
  RequestTimeTooSkewed: '服务器系统时间不准，请同步 NTP。',
};

function clientFallbackMessage(err?: string, detail?: OssTestFailureDetail | null): string {
  const http = detail?.http_code ?? 0;
  const ossCode = detail?.oss_code?.trim() ?? '';
  const ossMsg = detail?.oss_message?.trim() ?? '';
  const tail =
    ossCode || ossMsg
      ? `（${[ossCode, ossMsg].filter(Boolean).join('：')}）`
      : detail?.body
        ? ` 响应：${detail.body.replace(/\s+/g, ' ').slice(0, 160)}`
        : '';

  if (detail?.user_message?.trim()) {
    return detail.user_message.trim();
  }
  if (ossCode && OSS_CODE_HINTS[ossCode]) {
    return `${OSS_CODE_HINTS[ossCode]}${tail}`;
  }
  if (err === 'oss_incomplete_config') {
    return 'OSS 配置不完整，请填写 Endpoint、Bucket 与 AccessKey。';
  }
  if (err === 'oss_not_configured') {
    return '尚未保存 OSS 配置，请先填写并保存。';
  }
  if (http === 403) {
    return `阿里云拒绝访问（HTTP 403）${tail}。常见原因：RAM 策略未授予该 Bucket 写入权限。`;
  }
  if (http === 404) {
    return `Bucket 或路径不存在（HTTP 404）${tail}。请核对 Bucket 名称与 Endpoint 地域。`;
  }
  if (http === 0 && detail?.curl_error) {
    return `无法连接 OSS：${detail.curl_error}`;
  }
  if (http > 0) {
    return `OSS 测试失败（HTTP ${http}）${tail}`;
  }
  return err ? `OSS 测试失败（${err}）` : 'OSS 测试失败，请检查配置与 RAM 权限后重试。';
}

/** 展示「测试连接」失败原因（优先服务端 message / user_message） */
export function formatOssTestFailure(
  err?: string,
  detail?: OssTestFailureDetail | null,
  message?: string,
): string {
  const fromServer = (message ?? detail?.user_message ?? '').trim();
  if (fromServer) {
    return fromServer;
  }
  return clientFallbackMessage(err, detail);
}
